import React, { useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { ChevronRight, ChevronDown, ShieldCheck, ListChecks, Grid, ShoppingCart, Wallet, Truck, Globe } from "lucide-react";
import { PermissionGroupDef, PermissionModuleDef, PermissionActionDef } from "../types/role";

interface DynamicPermissionTreeProps {
  permissionsSchema: PermissionGroupDef[];
  activeModules: string[];
  permissions: any[];
  onChange: (newPermissions: any[]) => void;
  readOnly?: boolean;
}

const iconMap: Record<string, React.FC<any>> = {
  Grid,
  ShoppingCart,
  Wallet,
  Truck,
  Globe,
};

const colorMap: Record<string, string> = {
  canRead: 'bg-blue-500',
  canCreate: 'bg-green-500',
  canUpdate: 'bg-orange-500',
  canDelete: 'bg-red-500',
  canExport: 'bg-purple-500'
};

export const DynamicPermissionTree: React.FC<DynamicPermissionTreeProps> = ({
  permissionsSchema,
  activeModules,
  permissions,
  onChange,
  readOnly = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => prev.includes(groupKey) ? prev.filter(k => k !== groupKey) : [...prev, groupKey]);
  };

  const toggleModule = (moduleKey: string) => {
    setExpandedModules(prev => prev.includes(moduleKey) ? prev.filter(k => k !== moduleKey) : [...prev, moduleKey]);
  };

  const getVal = (moduleKey: string, actionKey: string) => {
    return permissions.find(p => p.module === moduleKey)?.[actionKey] || false;
  };

  const updatePermissionsWithDependencies = (
    currentPerms: any[],
    moduleKey: string,
    actionKey: string,
    value: boolean
  ): any[] => {
    let newPerms = [...currentPerms];
    
    let actionDef: PermissionActionDef | undefined;
    for (const group of permissionsSchema) {
      const mod = group.modules.find(m => m.moduleKey === moduleKey);
      if (mod) {
        actionDef = mod.actions.find(a => a.action === actionKey);
        break;
      }
    }

    const index = newPerms.findIndex(p => p.module === moduleKey);
    if (index > -1) {
      newPerms[index] = { ...newPerms[index], [actionKey]: value };
    } else {
      newPerms.push({
        module: moduleKey,
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canExport: false,
        [actionKey]: value,
      });
    }

    if (value && actionDef?.implies) {
      for (const dependency of actionDef.implies) {
        if (!getVal(dependency.module, dependency.action)) {
           newPerms = updatePermissionsWithDependencies(newPerms, dependency.module, dependency.action, true);
        }
      }
    }

    return newPerms;
  };

  const handleCheck = (moduleKey: string, actionKey: string, value: boolean) => {
    if (readOnly) return;
    const newPerms = updatePermissionsWithDependencies(permissions, moduleKey, actionKey, value);
    onChange(newPerms);
  };

  const toggleModuleAll = (e: React.MouseEvent, mod: PermissionModuleDef) => {
    e.stopPropagation();
    if (readOnly) return;

    const current = permissions.find(p => p.module === mod.moduleKey);
    const isAllChecked = mod.actions.every(a => current?.[a.action] === true);
    const newValue = !isAllChecked;

    let newPerms = [...permissions];
    
    if (newValue) {
      for (const action of mod.actions) {
        newPerms = updatePermissionsWithDependencies(newPerms, mod.moduleKey, action.action, true);
      }
    } else {
      const index = newPerms.findIndex(p => p.module === mod.moduleKey);
      if (index > -1) {
        newPerms[index] = {
           module: mod.moduleKey,
           canRead: false,
           canCreate: false,
           canUpdate: false,
           canDelete: false,
           canExport: false
        };
      }
    }

    onChange(newPerms);
  };

  const toggleGroupAll = (e: React.MouseEvent, group: PermissionGroupDef, filteredModules: PermissionModuleDef[]) => {
    e.stopPropagation();
    if (readOnly) return;

    // Check if ALL actions in ALL filtered modules are currently selected
    let allChecked = true;
    for (const mod of filteredModules) {
      for (const action of mod.actions) {
        if (!getVal(mod.moduleKey, action.action)) {
          allChecked = false;
          break;
        }
      }
      if (!allChecked) break;
    }

    const newValue = !allChecked;
    let newPerms = [...permissions];

    if (newValue) {
      // Check all
      for (const mod of filteredModules) {
        for (const action of mod.actions) {
          newPerms = updatePermissionsWithDependencies(newPerms, mod.moduleKey, action.action, true);
        }
      }
    } else {
      // Uncheck all
      for (const mod of filteredModules) {
        const index = newPerms.findIndex(p => p.module === mod.moduleKey);
        if (index > -1) {
          newPerms[index] = {
            module: mod.moduleKey,
            canRead: false,
            canCreate: false,
            canUpdate: false,
            canDelete: false,
            canExport: false
          };
        }
      }
    }

    onChange(newPerms);
  };

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
      {permissionsSchema.map(group => {
        // Core modules might not be in my-modules explicitly for some tenants, but they are always needed
        const alwaysVisibleModules = ["systemModule", "userModule"];
        const filteredModules = group.modules.filter(m => 
          activeModules.includes(m.moduleKey) || alwaysVisibleModules.includes(m.moduleKey)
        );

        if (filteredModules.length === 0) return null;

        let totalActions = 0;
        let selectedActions = 0;

        filteredModules.forEach(mod => {
          totalActions += mod.actions.length;
          mod.actions.forEach(a => {
            if (getVal(mod.moduleKey, a.action)) {
              selectedActions++;
            }
          });
        });

        const isAllSelected = totalActions > 0 && selectedActions === totalActions;
        const isPartiallySelected = selectedActions > 0 && selectedActions < totalActions;

        const GroupIcon = group.icon && iconMap[group.icon] ? iconMap[group.icon] : ShieldCheck;

        return (
          <div key={group.groupKey} className="border border-borderColor rounded-lg overflow-hidden shadow-sm">
            {/* Group Header */}
            <div 
              onClick={() => toggleGroup(group.groupKey)}
              className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${isAllSelected ? 'bg-primary/10' : 'bg-bgAlt hover:bg-bg'}`}
            >
              <div className="flex items-center gap-3">
                {expandedGroups.includes(group.groupKey) ? <ChevronDown size={20} className="text-primary"/> : <ChevronRight size={20} className="text-primary"/>}
                <GroupIcon size={20} className={isAllSelected ? "text-primary" : "text-secondary"}/>
                <div className="flex flex-col">
                  <span className="font-bold uppercase tracking-wide text-sm text-text">
                    {group.label}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-semibold opacity-70">
                      {filteredModules.length} Modül
                    </span>
                    <span className="w-1 h-1 rounded-full bg-secondary opacity-50"></span>
                    <span className={`text-[10px] font-bold ${isAllSelected ? 'text-primary' : isPartiallySelected ? 'text-yellow-500' : 'text-secondary'}`}>
                      {selectedActions}/{totalActions} Seçili
                    </span>
                  </div>
                </div>
              </div>
              
              {!readOnly && (
                <button
                  onClick={(e) => toggleGroupAll(e, group, filteredModules)}
                  className={`p-1.5 rounded-md transition-colors border ${isAllSelected ? 'bg-primary text-white border-primary hover:bg-primaryHover' : isPartiallySelected ? 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30 hover:bg-yellow-500/30' : 'text-text hover:text-primary border-transparent hover:border-borderColor'}`}
                  title={isAllSelected ? "Tümünü Kaldır" : "Tümünü Seç"}
                >
                  <ListChecks size={18} />
                </button>
              )}
            </div>

            {/* Modules List inside Group */}
            {expandedGroups.includes(group.groupKey) && (
              <div className="p-3 space-y-3 bg-card animate-in slide-in-from-top-2 duration-200">
                {filteredModules.map(mod => {
                  const displayLabel = mod.label || mod.moduleKey;
                  const isModExpanded = expandedModules.includes(mod.moduleKey);

                  return (
                    <div key={mod.moduleKey} className="border border-borderColor rounded-md overflow-hidden">
                      {/* Module Header */}
                      <div 
                        onClick={() => toggleModule(mod.moduleKey)}
                        className="flex items-center justify-between p-3 cursor-pointer bg-bgAlt hover:bg-bg transition-colors"
                      >
                        <div className="flex items-center gap-2 text-text">
                          {isModExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                          <ShieldCheck size={16} className={`${getVal(mod.moduleKey, 'canRead') ? 'text-primary' : 'text-secondary'}`}/>
                          <span className="font-semibold uppercase tracking-tight text-xs">
                            {displayLabel}
                          </span>
                        </div>

                        <div className="flex items-center gap-4">
                          {/* Status dots */}
                          <div className="flex gap-1">
                            {mod.actions.map(a => (
                              <div 
                                key={a.action} 
                                className={`w-1.5 h-1.5 rounded-full transition-all ${getVal(mod.moduleKey, a.action) ? (colorMap[a.action] || 'bg-primary') : 'bg-borderColor'}`} 
                                title={a.label}
                              />
                            ))}
                          </div>

                          {!readOnly && (
                            <button
                              onClick={(e) => toggleModuleAll(e, mod)}
                              className="p-1.5 rounded-md text-text hover:text-primary transition-colors border border-transparent hover:border-borderColor"
                              title="Tüm Yetkileri Seç/Kaldır"
                            >
                              <ListChecks size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Actions List inside Module */}
                      {isModExpanded && (
                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-card rounded-t-none border-t border-borderColor">
                          {mod.actions.map((action) => {
                            const isChecked = getVal(mod.moduleKey, action.action);
                            const activeColor = colorMap[action.action] || 'bg-primary';
                            
                            return (
                              <div 
                                key={action.action} 
                                onClick={() => !readOnly && handleCheck(mod.moduleKey, action.action, !isChecked)} 
                                className={`
                                  flex items-center justify-between p-2.5 rounded-md cursor-pointer transition-all border
                                  ${isChecked 
                                    ? 'bg-primary/10 border-primary/30 shadow-sm' 
                                    : 'bg-bg hover:bg-bgAlt border-transparent'}
                                `}
                              >
                                <div className="flex items-center gap-2 text-text">
                                  <div className={`w-1 h-3 rounded-full ${isChecked ? activeColor : 'bg-borderColor'}`} />
                                  <div className="flex flex-col">
                                    <label className="text-[10px] font-bold uppercase tracking-wider cursor-pointer">
                                      {action.label}
                                    </label>
                                    {action.implies && action.implies.length > 0 && (
                                      <span className="text-[9px] opacity-60">Bağımlılıklar içerir</span>
                                    )}
                                  </div>
                                </div>

                                <Checkbox
                                  disabled={readOnly}
                                  checked={isChecked}
                                  onChange={(e) => handleCheck(mod.moduleKey, action.action, e.checked || false)}
                                  onClick={(e) => e.stopPropagation()} 
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
